import type { JobDatesData } from './types';

export function formatDateForInput(dateString: string | null | undefined): string {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    if (dateString.includes('T') || dateString.includes(' ')) {
      return date.toISOString().slice(0, 16);
    }
    return date.toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

export interface JobDateValidationContext {
  allDates: {
    glass_arrival_date?: string | null;
    manufacture_date?: string | null;
    potential_delivery_date?: string | null;
  };
  hideGlassArrival: boolean;
  assignedDeliveryUserRole?: string | null;
}

/**
 * Validates a single field change against the full date tuple (same rules as JobDatesManager).
 */
export function validateJobDateField(
  field: keyof JobDatesData,
  value: string,
  ctx: JobDateValidationContext
): string | null {
  const { allDates, hideGlassArrival, assignedDeliveryUserRole } = ctx;
  const newDates = { ...allDates, [field]: value };

  if (field === 'glass_arrival_date' && hideGlassArrival) {
    return null;
  }

  if (!hideGlassArrival) {
    if (field === 'glass_arrival_date' && value && newDates.manufacture_date) {
      const glassDate = new Date(value);
      const manufDate = new Date(newDates.manufacture_date);
      if (glassDate > manufDate) {
        return 'Glass Arrival date cannot be after Manufacturing date';
      }
    }

    if (field === 'glass_arrival_date' && value && newDates.potential_delivery_date) {
      const glassDate = new Date(value);
      const potentialDate = new Date(newDates.potential_delivery_date);
      if (glassDate >= potentialDate) {
        return 'Glass Arrival date cannot be same as or after Potential Delivery date';
      }
    }

    if (field === 'manufacture_date' && value && newDates.glass_arrival_date) {
      const glassDate = new Date(newDates.glass_arrival_date);
      const manufDate = new Date(value);
      if (manufDate < glassDate) {
        return 'Manufacturing date cannot be before Glass Arrival date';
      }
    }
  }

  if (field === 'potential_delivery_date' && value) {
    const potentialDate = new Date(value);

    if (!hideGlassArrival && newDates.glass_arrival_date) {
      const glassDate = new Date(newDates.glass_arrival_date);
      if (potentialDate < glassDate) {
        return 'Potential Delivery date cannot be before Glass Arrival date';
      }
    }

    if (newDates.manufacture_date) {
      const manufDate = new Date(newDates.manufacture_date);
      if (potentialDate < manufDate) {
        return 'Potential Delivery date cannot be before Manufacturing date';
      }
    }

    if (newDates.manufacture_date) {
      const manufDate = new Date(newDates.manufacture_date);
      const isSameDay = potentialDate.toDateString() === manufDate.toDateString();

      if (isSameDay) {
        const userRoleName = assignedDeliveryUserRole?.toLowerCase() || '';
        const hasCollectionRole = userRoleName.includes('collection');

        if (!hasCollectionRole) {
          if (!assignedDeliveryUserRole) {
            return 'Potential Delivery can only be same as Manufacturing date if a Collection user is assigned';
          }
          return 'Potential Delivery can only be same as Manufacturing date if assigned user has Collection role (current user has Delivery role)';
        }
      }
    }
  }

  return null;
}

const JOB_DATE_FIELD_ORDER: (keyof JobDatesData)[] = [
  'glass_arrival_date',
  'manufacture_date',
  'potential_delivery_date',
];

/** Run per-field validation for the full tuple (e.g. before submit). */
export function validateAllJobDates(
  allDates: JobDateValidationContext['allDates'],
  options: Omit<JobDateValidationContext, 'allDates'>
): string | null {
  const ctx: JobDateValidationContext = { ...options, allDates };
  for (const field of JOB_DATE_FIELD_ORDER) {
    if (field === 'glass_arrival_date' && options.hideGlassArrival) {
      continue;
    }
    const value = String(allDates[field] ?? '');
    const err = validateJobDateField(field, value, ctx);
    if (err) return err;
  }
  return null;
}
